import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Performance metrics types
interface PerformanceMetric {
  timestamp: number;
  value: number;
  metadata?: Record<string, unknown>;
}

interface PageLoadMetric extends PerformanceMetric {
  page: string;
  loadTime: number;
}

interface ApiCallMetric extends PerformanceMetric {
  endpoint: string;
  method: string;
  duration: number;
  success: boolean;
}

interface CrashMetric {
  timestamp: number;
  error: string;
  stack?: string;
  component?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface UpdateLatencyMetric extends PerformanceMetric {
  operation: string;
  latency: number;
  isOptimistic: boolean;
}

interface PerformanceBaseline {
  capturedAt: number;
  loadTime: number;
  apiCallCount: number;
  crashRate: number;
  updateLatency: number;
}

interface PerformanceState {
  // Metrics collections
  pageLoads: PageLoadMetric[];
  apiCalls: ApiCallMetric[];
  crashes: CrashMetric[];
  updateLatencies: UpdateLatencyMetric[];
  
  // Baselines
  baseline: PerformanceBaseline | null;
  
  // Current session metrics
  currentSession: {
    startTime: number;
    pageLoadsCount: number;
    apiCallsCount: number;
    crashesCount: number;
    totalLoadTime: number;
    totalApiTime: number;
  };
  
  // Actions
  recordPageLoad: (page: string, loadTime: number, metadata?: Record<string, unknown>) => void;
  recordApiCall: (endpoint: string, method: string, duration: number, success: boolean) => void;
  recordCrash: (error: string, stack?: string, component?: string, severity?: 'low' | 'medium' | 'high' | 'critical') => void;
  recordUpdateLatency: (operation: string, latency: number, isOptimistic: boolean) => void;
  
  // Baseline management
  captureBaseline: () => void;
  
  // Analytics
  getAverageLoadTime: () => number;
  getApiCallsPerSession: () => number;
  getCrashRate: () => number;
  getAverageUpdateLatency: () => number;
  getMetricsSummary: () => PerformanceSummary;
  
  // Reset and cleanup
  clearMetrics: () => void;
  resetSession: () => void;
}

interface PerformanceSummary {
  avgLoadTime: number;
  totalApiCalls: number;
  avgApiCallsPerSession: number;
  crashesPerDay: number;
  avgUpdateLatency: number;
  baseline: PerformanceBaseline | null;
  improvements: {
    loadTimeImprovement: number | null;
    apiCallReduction: number | null;
    crashReduction: number | null;
    latencyImprovement: number | null;
  };
  targetMetrics: {
    loadTime: { current: number; target: number; met: boolean };
    apiCalls: { current: number; target: number; met: boolean };
    crashes: { current: number; target: number; met: boolean };
    updateLatency: { current: number; target: number; met: boolean };
  };
}

// Constants for target metrics
const TARGET_LOAD_TIME = 2100; // 2.1s in ms
const TARGET_API_CALLS = 8;
const TARGET_CRASHES = 0;
const TARGET_UPDATE_LATENCY = 500; // 500ms

// Maximum metrics to keep in memory (to prevent memory leaks)
const MAX_METRICS = 1000;

export const usePerformanceStore = create<PerformanceState>()(
  persist(
    (set, get) => ({
      pageLoads: [],
      apiCalls: [],
      crashes: [],
      updateLatencies: [],
      baseline: null,
      currentSession: {
        startTime: Date.now(),
        pageLoadsCount: 0,
        apiCallsCount: 0,
        crashesCount: 0,
        totalLoadTime: 0,
        totalApiTime: 0,
      },

      recordPageLoad: (page, loadTime, metadata) => {
        set((state) => {
          const newMetric: PageLoadMetric = {
            timestamp: Date.now(),
            value: loadTime,
            page,
            loadTime,
            metadata,
          };
          
          const pageLoads = [...state.pageLoads, newMetric].slice(-MAX_METRICS);
          
          return {
            pageLoads,
            currentSession: {
              ...state.currentSession,
              pageLoadsCount: state.currentSession.pageLoadsCount + 1,
              totalLoadTime: state.currentSession.totalLoadTime + loadTime,
            },
          };
        });
      },

      recordApiCall: (endpoint, method, duration, success) => {
        set((state) => {
          const newMetric: ApiCallMetric = {
            timestamp: Date.now(),
            value: duration,
            endpoint,
            method,
            duration,
            success,
          };
          
          const apiCalls = [...state.apiCalls, newMetric].slice(-MAX_METRICS);
          
          return {
            apiCalls,
            currentSession: {
              ...state.currentSession,
              apiCallsCount: state.currentSession.apiCallsCount + 1,
              totalApiTime: state.currentSession.totalApiTime + duration,
            },
          };
        });
      },

      recordCrash: (error, stack, component, severity = 'medium') => {
        set((state) => {
          const newCrash: CrashMetric = {
            timestamp: Date.now(),
            error,
            stack,
            component,
            severity,
          };
          
          const crashes = [...state.crashes, newCrash].slice(-MAX_METRICS);
          
          return {
            crashes,
            currentSession: {
              ...state.currentSession,
              crashesCount: state.currentSession.crashesCount + 1,
            },
          };
        });
      },

      recordUpdateLatency: (operation, latency, isOptimistic) => {
        set((state) => {
          const newMetric: UpdateLatencyMetric = {
            timestamp: Date.now(),
            value: latency,
            operation,
            latency,
            isOptimistic,
          };
          
          const updateLatencies = [...state.updateLatencies, newMetric].slice(-MAX_METRICS);
          
          return {
            updateLatencies,
          };
        });
      },

      captureBaseline: () => {
        const state = get();
        const baseline: PerformanceBaseline = {
          capturedAt: Date.now(),
          loadTime: state.getAverageLoadTime(),
          apiCallCount: state.getApiCallsPerSession(),
          crashRate: state.getCrashRate(),
          updateLatency: state.getAverageUpdateLatency(),
        };
        
        set({ baseline });
      },

      getAverageLoadTime: () => {
        const state = get();
        if (state.pageLoads.length === 0) return 0;
        
        const total = state.pageLoads.reduce((sum, metric) => sum + metric.loadTime, 0);
        return total / state.pageLoads.length;
      },

      getApiCallsPerSession: () => {
        const state = get();
        return state.currentSession.apiCallsCount;
      },

      getCrashRate: () => {
        const state = get();
        const now = Date.now();
        const oneDayAgo = now - 24 * 60 * 60 * 1000;
        
        const crashesInLastDay = state.crashes.filter(
          crash => crash.timestamp >= oneDayAgo
        );
        
        return crashesInLastDay.length;
      },

      getAverageUpdateLatency: () => {
        const state = get();
        if (state.updateLatencies.length === 0) return 0;
        
        const total = state.updateLatencies.reduce((sum, metric) => sum + metric.latency, 0);
        return total / state.updateLatencies.length;
      },

      getMetricsSummary: () => {
        const state = get();
        const avgLoadTime = state.getAverageLoadTime();
        const apiCallsPerSession = state.getApiCallsPerSession();
        const crashesPerDay = state.getCrashRate();
        const avgUpdateLatency = state.getAverageUpdateLatency();
        
        const improvements = {
          loadTimeImprovement: state.baseline 
            ? ((state.baseline.loadTime - avgLoadTime) / state.baseline.loadTime) * 100
            : null,
          apiCallReduction: state.baseline 
            ? ((state.baseline.apiCallCount - apiCallsPerSession) / state.baseline.apiCallCount) * 100
            : null,
          crashReduction: state.baseline 
            ? ((state.baseline.crashRate - crashesPerDay) / (state.baseline.crashRate || 1)) * 100
            : null,
          latencyImprovement: state.baseline 
            ? ((state.baseline.updateLatency - avgUpdateLatency) / state.baseline.updateLatency) * 100
            : null,
        };
        
        const targetMetrics = {
          loadTime: {
            current: avgLoadTime,
            target: TARGET_LOAD_TIME,
            met: avgLoadTime <= TARGET_LOAD_TIME && avgLoadTime > 0,
          },
          apiCalls: {
            current: apiCallsPerSession,
            target: TARGET_API_CALLS,
            met: apiCallsPerSession <= TARGET_API_CALLS,
          },
          crashes: {
            current: crashesPerDay,
            target: TARGET_CRASHES,
            met: crashesPerDay <= TARGET_CRASHES,
          },
          updateLatency: {
            current: avgUpdateLatency,
            target: TARGET_UPDATE_LATENCY,
            met: avgUpdateLatency <= TARGET_UPDATE_LATENCY && avgUpdateLatency > 0,
          },
        };
        
        return {
          avgLoadTime,
          totalApiCalls: state.apiCalls.length,
          avgApiCallsPerSession: apiCallsPerSession,
          crashesPerDay,
          avgUpdateLatency,
          baseline: state.baseline,
          improvements,
          targetMetrics,
        };
      },

      clearMetrics: () => {
        set({
          pageLoads: [],
          apiCalls: [],
          crashes: [],
          updateLatencies: [],
        });
      },

      resetSession: () => {
        set({
          currentSession: {
            startTime: Date.now(),
            pageLoadsCount: 0,
            apiCallsCount: 0,
            crashesCount: 0,
            totalLoadTime: 0,
            totalApiTime: 0,
          },
        });
      },
    }),
    {
      name: 'performance-metrics',
      partialize: (state) => ({
        pageLoads: state.pageLoads,
        apiCalls: state.apiCalls,
        crashes: state.crashes,
        updateLatencies: state.updateLatencies,
        baseline: state.baseline,
      }),
    }
  )
);
